import { useEffect, useRef } from 'react'
import g1 from './assets/cadran1.png'
import g2 from './assets/cadran2.png'
import g3 from './assets/cadran3.png'
import g4 from './assets/cadran4.png'
import g5 from './assets/cadran5.png'
import g6 from './assets/cadran6.png'
import LogoAnim from './LogoAnim.jsx'
import { MOMENTE } from './momente.js'
import MomentCap, { DiscordIcon } from './MomentCap.jsx'
import { ramp, makeScrubber, showCap } from './scrub.js'

const tiles = [
    { img: g1, label: 'Telefon iOS', icon: <svg viewBox="0 0 24 24"><rect x="6" y="2" width="12" height="20" rx="2.5"/><line x1="10" y1="18.5" x2="14" y2="18.5"/></svg> },
    { img: g2, label: 'Vehicule & Dealership', icon: <svg viewBox="0 0 24 24"><path d="M4 13l1.5-5a2 2 0 0 1 2-1.5h9a2 2 0 0 1 2 1.5L20 13v5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H7v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z"/><circle cx="7.5" cy="15" r="1"/><circle cx="16.5" cy="15" r="1"/></svg> },
    { img: g3, label: 'Facțiuni & Joburi', icon: <svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6Z"/></svg> },
    { img: g4, label: 'Economie & Bancă', icon: <svg viewBox="0 0 24 24"><path d="M3 9l9-5 9 5"/><line x1="4" y1="10" x2="4" y2="19"/><line x1="9" y1="10" x2="9" y2="19"/><line x1="15" y1="10" x2="15" y2="19"/><line x1="20" y1="10" x2="20" y2="19"/><line x1="3" y1="20" x2="21" y2="20"/></svg> },
    { img: g5, label: 'Case & Bizuri', icon: <svg viewBox="0 0 24 24"><path d="M4 20V9l5-4 5 4v11"/><path d="M14 20V12l6-3v11"/><line x1="2" y1="20" x2="22" y2="20"/></svg> },
    { img: g6, label: 'Creator Personaj', icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M5 21c0-3.5 3-6 7-6s7 2.5 7 6"/></svg> },
]

// Momentele din hero: toate în afară de final (semnul Vinewood are scena lui, după Despre și Gameplay)
const HERO = MOMENTE.map((m, i) => i).filter((i) => !MOMENTE[i].final)

// Timeline-ul de scroll (în ecrane de scroll, 1 = 100vh):
//   0 .. 0.13            mozaicul face fade out, primul clip apare
//   fiecare moment = SEG: clipul se derulează până la 60 %, apoi se întunecă și apare textul;
//                         de la 80 % (când pleacă textul) următorul clip pornește deja și intră peste el
//                         în cross-fade (84 %..100 %), ca să nu fie niciun cadru static între ele
//   un clip se derulează pe x ∈ [-0.2, 0.6] din segmentul lui, adică începe în coada segmentului anterior
//   END .. END + FADE    toată scena face fade out și apare secțiunea Despre (suprapusă prin .over)
const SEG = 0.8
const FADE = 0.35
const N = HERO.length
const END = N * SEG
// .over are margin-top:-130vh; Despre ajunge la ~15vh de sus exact când scena a dispărut
const CINE_VH = Math.round((END + FADE) * 100 + 145)

const Hero = () => {
    const cineRef = useRef(null)
    const heroRef = useRef(null)
    const stackRef = useRef(null)

    useEffect(() => {
        const reduce = matchMedia('(prefers-reduced-motion:reduce)').matches
        const q = (s) => document.querySelector(s)
        const cine = cineRef.current, hero = heroRef.current, stack = stackRef.current
        const vids = [...stack.querySelectorAll('video')]
        const caps = [...stack.querySelectorAll('.moment-cap')]
        const shade = stack.querySelector('.moment-shade')
        const cue = q('#cue'), loader = q('#loader'), stage = q('#stage'), nav = q('#nav')
        let revealed = false, raf = 0, tmr = 0
        const sc = makeScrubber(vids, HERO)

        // cât s-a derulat din scenă, în ecrane
        const scrolled = () => {
            if (!cine) return 0
            return Math.max(0, -cine.getBoundingClientRect().top) / window.innerHeight
        }

        let lastI = -1
        const loop = () => {
            if (revealed) {
                const u = scrolled()
                const heroOp = 1 - ramp(0, 0.13, u)
                const stageOp = 1 - ramp(END, END + FADE, u)
                if (hero) { hero.style.opacity = heroOp; hero.style.pointerEvents = heroOp < 0.05 ? 'none' : 'auto' }
                if (cue) cue.style.opacity = Math.min(heroOp, stageOp)
                if (stage) { stage.style.opacity = stageOp; stage.style.pointerEvents = stageOp < 0.05 ? 'none' : 'auto' }

                const i = Math.min(N - 1, Math.floor(u / SEG))
                const x = ramp(0, 1, (u - i * SEG) / SEG)
                const last = i === N - 1
                if (i !== lastI) { lastI = i; for (let k = i; k <= i + 2 && k < N; k++) sc.load(k) }

                const next = last ? 0 : ramp(0.84, 1, x)
                vids.forEach((v, k) => {
                    let op = 0
                    if (k === i) op = k === 0 ? ramp(0, 0.08, u) : 1
                    else if (k === i + 1) op = next
                    v.style.opacity = op
                })
                sc.seek(i, i === 0 ? ramp(0, 0.6, x) : ramp(-0.2, 0.6, x))
                if (!last && x >= 0.8) sc.seek(i + 1, ramp(0.8, 1.6, x))

                // după clip: cadrul final se întunecă și apare textul; ultimul pleacă la începutul fade-ului
                // scenei, ca să nu se suprapună cu titlul din Despre
                const out = last ? ramp(END, END + 0.15, u) : ramp(0.8, 0.9, x)
                if (shade) shade.style.opacity = ramp(0.5, 0.66, x) * (1 - next)
                caps.forEach((c, k) => showCap(c, k === i ? ramp(0.52, 0.68, x) * (1 - out) : 0, ramp(0.52, 0.74, x)))
            }
            raf = requestAnimationFrame(loop)
        }

        const doReveal = () => {
            if (loader) loader.classList.add('gone')
            if (stage) stage.classList.add('revealed')
            setTimeout(() => {
                if (hero) hero.classList.add('show')
                if (cue) cue.classList.add('show')
                if (nav) nav.classList.add('ready')
                revealed = true
                document.body.classList.remove('locked')
                setTimeout(() => { if (loader) loader.style.display = 'none' }, 900)
            }, 500)
        }

        sc.load(0); sc.load(1)

        document.body.classList.add('locked')
        if (reduce) {
            if (loader) loader.style.display = 'none'
            if (stage) stage.classList.add('revealed')
            if (hero) hero.classList.add('show')
            if (cue) cue.classList.add('show')
            if (nav) nav.classList.add('ready')
            revealed = true
            document.body.classList.remove('locked')
        } else {
            // 2700: lasă animația logo-ului să aterizeze (1,25 s) și luciul să treacă peste litere
            tmr = setTimeout(doReveal, 2700)
        }
        raf = requestAnimationFrame(loop)

        return () => {
            cancelAnimationFrame(raf); clearTimeout(tmr)
            sc.stop()
            document.body.classList.remove('locked')
        }
    }, [])

    return (
        <div className="cine" ref={cineRef} style={{ height: CINE_VH + 'vh' }}>
            <div className="stage" id="stage">
                <div className="bg"><div className="aurora a1"></div><div className="aurora a2"></div><div className="aurora a3"></div><div className="aurora a4"></div></div>
                <div className="decor" id="decor">
                    <div className="rays"></div><div className="sunorb"></div><div className="grid"></div>
                    <svg className="palm left" viewBox="0 0 200 300"><path fill="#0a0512" d="M98 300c-4-70-6-130-2-175-22-14-52-20-86-14 30-10 64-12 88-2-2-16-8-30-18-44 18 10 28 26 32 44 8-22 26-40 54-50-22 16-36 34-40 54 22-12 50-16 82-8-34 2-62 12-80 28 4 46 2 106-2 177z"/></svg>
                    <svg className="palm right" viewBox="0 0 200 300"><path fill="#0a0512" d="M98 300c-4-70-6-130-2-175-22-14-52-20-86-14 30-10 64-12 88-2-2-16-8-30-18-44 18 10 28 26 32 44 8-22 26-40 54-50-22 16-36 34-40 54 22-12 50-16 82-8-34 2-62 12-80 28 4 46 2 106-2 177z"/></svg>
                </div>
                <div className="stage-vignette"></div>

                <div className="momente" ref={stackRef}>
                    {HERO.map((i) => (
                        <video key={i} className="cine-video" poster={MOMENTE[i].poster} muted playsInline webkit-playsinline="true" preload="none" />
                    ))}
                    <div className="moment-shade"></div>
                    {HERO.map((i) => <MomentCap key={i} i={i} />)}
                </div>

                <div className="hero" id="hero" ref={heroRef}>
                    <div className="poster r">
                        <div className="mosaic">
                            {tiles.map((t, i) => (
                                <div key={i} className="mtile" style={{ backgroundImage: `linear-gradient(160deg,rgba(139,63,240,.22),rgba(195,10,239,.1)), url(${t.img})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
                            ))}
                        </div>
                        <div className="poster-logo">
                            <div className="plemblem"><div className="glogo"></div></div>
                        </div>
                    </div>
                    <div className="hero-foot r">
                        <div className="hf-left"><span className="k">Coming</span><span className="v grad-text">Q1 2027</span></div>
                        <div className="hf-center">
                            <a className="btn btn-primary hf-cta" href="https://vinewood.ro/invite" target="_blank" rel="noreferrer">
                                <DiscordIcon /> Intră pe Discord
                            </a>
                        </div>
                        <div className="hf-right">
                            <span className="hf-only">Built on</span>
                            <span className="hf-fivem grad-text">FiveM</span>
                        </div>
                    </div>
                </div>
                <div className="scroll-cue" id="cue"><svg width="22" height="12" viewBox="0 0 22 12" fill="none"><path d="M1 1l10 9 10-9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>

                <div className="loader" id="loader">
                    <div className="emblem anim"><LogoAnim /></div>
                    <div className="tagline"><span className="d"></span> LOS SANTOS <span className="d"></span> ROMÂNIA <span className="d"></span> RPG <span className="d"></span></div>
                </div>
            </div>
        </div>
    )
}

export default Hero
